// src/views/tenants/EditTenantForm.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header.jsx";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config.js";
import formOptions from "../../config/formOptions.json";
import TenantBrandingPreview from "../../components/TenantBrandingPreview.jsx";

import { READ_TENANT_BY_ID } from "../../graph/tenants/queries.js";
import { UPDATE_TENANT } from "../../graph/tenants/mutations.js";

const client = new GraphQLClient(graphqlEndpoint);


const validationSchema = yup.object().shape({
  name: yup.string().trim().required("Tenant name is required"),
  status: yup.string().required("Status is required"),
  logoUrl: yup.string().nullable().url("Logo URL must be a valid URL"),
primaryColor: yup
  .string()
  .nullable()
  .matches(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/, "Use a valid hex colour"),
secondaryColor: yup
  .string()
  .nullable()
  .matches(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/, "Use a valid hex colour"),
  slug: yup
    .string()
    .nullable()
    .matches(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Slug must be lowercase letters, numbers, and hyphens only"
    ),
});

const EditTenantForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();

  const redirectPath = location.state?.redirectPath || "/manageTenants";
  const [initialValues, setInitialValues] = useState(null);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const { readTenantByID } = await client.request(READ_TENANT_BY_ID, { id });

        setInitialValues({
          id: readTenantByID.id,
          name: readTenantByID.name || "",
          status: readTenantByID.status || "",
          slug: readTenantByID.slug || "",
          logoUrl: readTenantByID.logoUrl || "",
          primaryColor: readTenantByID.primaryColor || "",
          secondaryColor: readTenantByID.secondaryColor || "",
          archivedAt: readTenantByID.archivedAt || null,
        });
      } catch (err) {
        console.error("Failed to fetch tenant:", err);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load tenant",
        });
      }
    };

    fetchTenant();
  }, [id, setSnackbar]);

  const handleFormSubmit = async (values) => {
    const input = {
      id: values.id,
      name: values.name.trim(),
      status: values.status,
      slug: values.slug?.trim() || null,
      logoUrl: values.logoUrl || null,
      primaryColor: values.primaryColor || null,
      secondaryColor: values.secondaryColor || null,
    };

    console.log("Updating tenant input:", input);

    try {
      await client.request(UPDATE_TENANT, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Tenant updated successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error("Failed to update tenant:", err?.response?.errors || err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update tenant",
      });
    }
  };

  if (!initialValues) return <div>Loading...</div>;

  return (
    <Box>
      <Header
        title="EDIT TENANT"
        subtitle={`Update tenant: ${initialValues.name}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Tenant Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl
                fullWidth
                sx={{ gridColumn: "span 2" }}
                error={!!touched.status && !!errors.status}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Status"
                >
                  {formOptions.tenantStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Slug"
                name="slug"
                value={values.slug}
                onChange={(e) => {
                  e.target.value = e.target.value.trim().toLowerCase();
                  handleChange(e);
                }}
                onBlur={handleBlur}
                error={!!touched.slug && !!errors.slug}
                helperText={touched.slug && errors.slug}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Logo URL"
                name="logoUrl"
                value={values.logoUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.logoUrl && !!errors.logoUrl}
                helperText={touched.logoUrl && errors.logoUrl}
                sx={{ gridColumn: "span 2" }}
              />

              <Box display="flex" gap={2} alignItems="center">
                <TextField
                  label="Primary Colour"
                  name="primaryColor"
                  value={values.primaryColor}
                  onChange={handleChange}
                  placeholder="#066873"
                />

                <input
                  type="color"
                  value={values.primaryColor || "#066873"}
                  onChange={(e) =>
                    setFieldValue("primaryColor", e.target.value)
                  }
                />
              </Box>

                            <Box display="flex" gap={2} alignItems="center">
                <TextField
                  label="Secondary Colour"
                  name="secondaryColor"
                  value={values.secondaryColor}
                  onChange={handleChange}
                  placeholder="#ba8700"
                />

                <input
                  type="color"
                  value={values.secondaryColor || "#ba8700"}
                  onChange={(e) =>
                    setFieldValue("secondaryColor", e.target.value)
                  }
                />
              </Box>

              {values.archivedAt && (
                <Box sx={{ gridColumn: "span 4" }}>
                  <Typography variant="body2" color="warning.main">
                    This tenant is archived and should generally not be edited.
                  </Typography>
                </Box>
              )}

              <Box sx={{ gridColumn: "span 4" }}>
                <TenantBrandingPreview
                  tenantName={values.name}
                  logoUrl={values.logoUrl}
                  primaryColor={values.primaryColor}
                  secondaryColor={values.secondaryColor}
                />
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton type="submit" tone="success">
                Save Changes
              </ActionButton>
            </Box>
            
          </form>
        )}
        
      </Formik>

    </Box>
  );
};

export default EditTenantForm;